/**
 * Player actions.
 *
 * Every action follows the same contract: it first ticks the state to `now`
 * (so decisions are made against current resources), then validates, then
 * mutates the ticked clone. Invalid actions throw `ActionError` with a
 * player-readable German message — the UI shows it verbatim.
 */

import type {
  BuildingKey,
  DefenseKey,
  EngineEvent,
  GameState,
  FleetMission,
  PlanetState,
  QueueJob,
  ResearchKey,
  ShipKey,
  TalentPathKey,
  TickResult,
  TradeableResource,
  UnitKey,
} from './types.js';
import { BUILDINGS } from './data/buildings.js';
import { RESEARCH } from './data/research.js';
import { UNITS } from './data/units.js';
import { RINGS, canTargetInRing } from './data/rings.js';
import { TALENT_MAX_PER_PATH } from './data/talents.js';
import {
  completionCost,
  buildTimeHours,
  fleetHangarCapacity,
  travelDistance,
  levelCost,
  researchLevelCost,
  researchTimeHours,
  tradeRelayCost,
  unitTimeHours,
  fleetTravelTime,
} from './formulas.js';
import { computeModifiers } from './modifiers.js';
import { buildingLevel, canAfford, createColonyPlanet, payCost, addResources, totalResearchLabLevel } from './state.js';
import { grantXp, grantXpForSpending } from './progression.js';
import { tick } from './tick.js';
import { nextInt, nextRandom } from './rng.js';
import { RESOURCE_LABELS, buyPrice, marketUnlocked, sellValue } from './market.js';
import { CAMP_TRAVEL_MS } from './espionage.js';
import { talentEffects } from './data/talents.js';
import {
  CASINO_GAME_MIN_LEVEL,
  MIN_STAKE,
  playHighLow,
  remainingStake,
  rollBlueprint,
  rolloverDay,
  spinRoulette,
  spinSlots,
  type CasinoGame,
  type RouletteBet,
} from './casino.js';
import {
  CHRONOS_DAILY_CAP,
  SHOP_PRODUCTS,
  productGrant,
  remainingDailyChronos,
  rolloverShopDay,
  type ShopProduct,
} from './shop.js';

export class ActionError extends Error {}

const MAX_QUEUE_LENGTH = 3;

/** Round trip of a mining fleet to an asteroid or a debris field. */
export const HARVEST_TRIP_MS = 30 * 60_000;

/** Travel duration after the `flightSpeed` modifier (event buffs, GDD §2). */
function travelMs(state: GameState, baseMs: number, now: number, fleet: Partial<Record<ShipKey, number>>): number {
  return fleetTravelTime(baseMs, fleet, computeModifiers(state, now).flightSpeed);
}

function requirementsMet(
  state: GameState,
  requires?: Partial<{
    buildings: Partial<Record<BuildingKey, number>>;
    research: Partial<Record<ResearchKey, number>>;
  }>,
  planetIndex = 0,
): boolean {
  if (!requires) return true;
  for (const [key, level] of Object.entries(requires.buildings ?? {})) {
    if (buildingLevel(state, key as BuildingKey, planetIndex) < (level as number)) return false;
  }
  for (const [key, level] of Object.entries(requires.research ?? {})) {
    if (state.research[key as ResearchKey] < (level as number)) return false;
  }
  return true;
}

/** Target level of `key` on `planetIndex`, including queued-but-unfinished levels. */
function pendingLevel(state: GameState, key: BuildingKey, planetIndex = 0): number {
  let level = buildingLevel(state, key, planetIndex);
  for (const job of state.planets[planetIndex]!.buildQueue) {
    if (job.key === key) level = Math.max(level, job.amount);
  }
  return level;
}

function pendingResearchLevel(state: GameState, key: ResearchKey): number {
  let level = state.research[key];
  for (const job of state.researchQueue) {
    if (job.key === key) level = Math.max(level, job.amount);
  }
  return level;
}

function enqueue(queue: QueueJob[], job: QueueJob, now: number): void {
  queue.push(job);
  if (queue[0] === job) {
    // Slot free — start immediately. finishesAt currently holds the duration.
    job.startedAt = now;
    job.finishesAt = now + job.finishesAt;
  }
}

/* ------------------------------------------------------------------ *
 * Building construction                                               *
 * ------------------------------------------------------------------ */

/**
 * `planetIndex` defaults to the home planet — Naniten (`nanoFactory`) only
 * speed up the home planet's own queue (galaxy plan Milestone E, see
 * `totalResearchLabLevel`'s doc comment for the empire-wide counterpart);
 * every other colony builds at the un-boosted base rate.
 */
export function upgradeBuilding(input: GameState, key: BuildingKey, now: number, planetIndex = 0): TickResult {
  const { state, events } = tick(input, now);
  const def = BUILDINGS[key];
  const planet = state.planets[planetIndex];
  if (!planet) throw new ActionError('Planet nicht gefunden.');

  if (planet.buildQueue.length >= MAX_QUEUE_LENGTH) {
    throw new ActionError('Die Bau-Warteschlange ist voll (max. 3 Aufträge).');
  }
  if (!requirementsMet(state, def.requires, planetIndex)) {
    throw new ActionError(`Voraussetzungen für ${def.label} sind nicht erfüllt.`);
  }
  if (planet.damaged.includes(key)) {
    throw new ActionError(`${def.label} ist beschädigt und muss erst repariert werden.`);
  }

  const targetLevel = pendingLevel(state, key, planetIndex) + 1;
  const cost = key === 'tradeRelay'
    ? tradeRelayCost(targetLevel, def.baseCost)
    : levelCost(def.baseCost, targetLevel);
  if (!canAfford(state, cost)) {
    throw new ActionError(`Nicht genug Rohstoffe für ${def.label} Stufe ${targetLevel}.`);
  }

  payCost(state, cost);
  events.push(...grantXpForSpending(state, cost, now));

  const nanoLevel = planetIndex === 0 ? buildingLevel(state, 'nanoFactory') : 0;
  const durationMs =
    buildTimeHours(cost, nanoLevel, computeModifiers(state, now).buildSpeed) * 3_600_000;
  const job: QueueJob = {
    id: state.nextId++,
    kind: 'building',
    key,
    amount: targetLevel,
    startedAt: 0,
    finishesAt: Math.max(1_000, Math.round(durationMs)),
    cost,
  };
  enqueue(planet.buildQueue, job, now);
  return { state, events };
}

/** Every queue a job can sit in: build queues on every planet, plus the shared research/shipyard queues. */
function allQueues(state: GameState): QueueJob[][] {
  return [state.researchQueue, ...state.planets.map((p) => p.shipyardQueue), ...state.planets.map((p) => p.buildQueue)];
}

/** Cancel a queued/running job; the paid cost is refunded in full. */
export function cancelJob(input: GameState, jobId: number, now: number): TickResult {
  const { state, events } = tick(input, now);
  for (const queue of allQueues(state)) {
    const index = queue.findIndex((j) => j.id === jobId);
    if (index >= 0) {
      const [job] = queue.splice(index, 1);
      addResources(state, job!.cost);
      const head = queue[0];
      if (head && head.startedAt === 0) {
        head.startedAt = now;
        head.finishesAt = now + head.finishesAt;
      }
      return { state, events };
    }
  }
  throw new ActionError('Auftrag nicht gefunden.');
}

/** The running job with this id, plus the queue it sits in. */
export function findJob(state: GameState, jobId: number): QueueJob | undefined {
  for (const queue of allQueues(state)) {
    const job = queue.find((j) => j.id === jobId);
    if (job) return job;
  }
  return undefined;
}

/**
 * What finishing this job right now would cost in Chronos crystals.
 *
 * The UI must show THIS number on the button and in the confirmation dialog:
 * the owner's rule is that a premium price is always the full completion price,
 * never a fraction of it.
 */
export function jobCompletionCost(state: GameState, jobId: number, now: number): number {
  const job = findJob(state, jobId);
  if (!job || job.startedAt === 0) return 0;
  return completionCost(job.finishesAt - now);
}

/**
 * Chronos boost: FINISHES the job. The price charged is exactly the price the
 * UI showed (`jobCompletionCost`), and the whole remaining time disappears.
 *
 * This intentionally departs from the GDD's 50 %-of-remaining-time cap: paying
 * premium currency for half an outcome is the part players reliably feel
 * cheated by. Spending is never implicit — the client asks for confirmation
 * before calling this.
 */
export function boostJob(input: GameState, jobId: number, now: number): TickResult {
  const { state, events } = tick(input, now);
  const job = findJob(state, jobId);
  if (!job) throw new ActionError('Auftrag nicht gefunden.');
  if (job.startedAt === 0) throw new ActionError('Wartende Aufträge können nicht beschleunigt werden.');

  const remaining = job.finishesAt - now;
  if (remaining <= 0) throw new ActionError('Der Auftrag ist bereits fertig.');
  const price = completionCost(remaining);
  if (state.resources.chronos < price) {
    throw new ActionError(`Nicht genug Chronos-Kristalle (benötigt: ${price}).`);
  }

  state.resources.chronos -= price;
  // Land it one millisecond in the past so the tick below completes it here
  // rather than leaving a finished job sitting in the queue for a frame.
  job.finishesAt = now - 1;
  const done = tick(state, now + 1);
  return { state: done.state, events: [...events, ...done.events] };
}

/* ------------------------------------------------------------------ *
 * Research                                                            *
 * ------------------------------------------------------------------ */

export function startResearch(input: GameState, key: ResearchKey, now: number): TickResult {
  const { state, events } = tick(input, now);
  const def = RESEARCH[key];

  if (state.researchQueue.length >= 1) {
    throw new ActionError('Es läuft bereits eine Forschung.');
  }
  if (!requirementsMet(state, def.requires)) {
    throw new ActionError(`Voraussetzungen für ${def.label} sind nicht erfüllt.`);
  }
  const targetLevel = pendingResearchLevel(state, key) + 1;
  if (def.maxLevel && targetLevel > def.maxLevel) {
    throw new ActionError(`${def.label} ist bereits auf Maximalstufe.`);
  }
  const cost = researchLevelCost(def.baseCost, def.factor, targetLevel);
  if (!canAfford(state, cost)) {
    throw new ActionError(`Nicht genug Rohstoffe für ${def.label} Stufe ${targetLevel}.`);
  }

  payCost(state, cost);
  events.push(...grantXpForSpending(state, cost, now));

  const durationMs =
    researchTimeHours(cost, totalResearchLabLevel(state), computeModifiers(state, now).researchSpeed) *
    3_600_000;
  const job: QueueJob = {
    id: state.nextId++,
    kind: 'research',
    key,
    amount: targetLevel,
    startedAt: 0,
    finishesAt: Math.max(1_000, Math.round(durationMs)),
    cost,
  };
  enqueue(state.researchQueue, job, now);
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Shipyard                                                            *
 * ------------------------------------------------------------------ */

export function buildUnits(input: GameState, key: UnitKey, count: number, now: number, planetIndex = 0): TickResult {
  const { state, events } = tick(input, now);
  const planet = state.planets[planetIndex];
  if (!planet) throw new ActionError('Planet nicht gefunden.');
  const def = UNITS[key];
  if (count < 1 || !Number.isInteger(count)) throw new ActionError('Ungültige Stückzahl.');
  if (def.eventOnly) {
    // Event ships need one blueprint per hull — the only way they enter a fleet.
    const held = state.blueprints[key as ShipKey] ?? 0;
    if (held < count) {
      throw new ActionError(
        `${def.label}: ${count} Baupläne nötig, ${held} vorhanden. Baupläne gibt es im Casino.`,
      );
    }
  }
  if (!requirementsMet(state, def.requires, planetIndex)) {
    throw new ActionError(`Voraussetzungen für ${def.label} sind nicht erfüllt.`);
  }
  if (def.maxCount !== undefined) {
    const existing = def.kind === 'ship'
      ? planet.ships[key as ShipKey]
      : planet.defense[key as DefenseKey];
    const queued = planet.shipyardQueue
      .filter((j) => j.key === key)
      .reduce((sum, j) => sum + j.amount, 0);
    if (existing + queued + count > def.maxCount) {
      throw new ActionError(`${def.label}: Maximalanzahl ${def.maxCount} erreicht.`);
    }
  }

  const cost = Object.fromEntries(
    Object.entries(def.cost).map(([r, v]) => [r, v * count]),
  ) as QueueJob['cost'];
  if (!canAfford(state, cost)) {
    throw new ActionError(`Nicht genug Rohstoffe für ${count}× ${def.label}.`);
  }

  payCost(state, cost);
  if (def.eventOnly) {
    state.blueprints[key as ShipKey] = (state.blueprints[key as ShipKey] ?? 0) - count;
  }
  events.push(...grantXpForSpending(state, cost, now));

  const perUnitMs =
    unitTimeHours(
      def.cost,
      planetIndex === 0 ? buildingLevel(state, 'nanoFactory') : 0,
      computeModifiers(state, now).shipyardSpeed,
    ) *
    3_600_000;
  const job: QueueJob = {
    id: state.nextId++,
    kind: def.kind === 'ship' ? 'ship' : 'defense',
    key,
    amount: count,
    startedAt: 0,
    finishesAt: Math.max(1_000, Math.round(perUnitMs * count)),
    cost,
  };
  enqueue(planet.shipyardQueue, job, now);
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Repairs (comet damage — GDD §3)                                     *
 * ------------------------------------------------------------------ */

/** Repair costs 25 % of the building's current-level cost in Titan+Silikat. */
export function repairBuilding(input: GameState, key: BuildingKey, now: number): TickResult {
  const { state, events } = tick(input, now);
  const index = state.planets[0]!.damaged.indexOf(key);
  if (index < 0) throw new ActionError('Dieses Gebäude ist nicht beschädigt.');
  const def = BUILDINGS[key];
  const full = levelCost(def.baseCost, Math.max(1, buildingLevel(state, key)));
  const cost = {
    titan: Math.round((full.titan ?? 0) * 0.25),
    silicate: Math.round((full.silicate ?? 0) * 0.25),
  };
  if (!canAfford(state, cost)) throw new ActionError(`Nicht genug Rohstoffe für die Reparatur von ${def.label}.`);
  payCost(state, cost);
  state.planets[0]!.damaged.splice(index, 1);
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Sector events (GDD §3)                                              *
 * ------------------------------------------------------------------ */

/** Intercept the comet: consumes one interceptor missile, grants XP. */
export function interceptComet(input: GameState, now: number): TickResult {
  const { state, events } = tick(input, now);
  const event = state.event;
  if (!event || event.type !== 'comet') throw new ActionError('Kein Komet im Anflug.');
  if (state.planets[0]!.defense.interceptor < 1) {
    throw new ActionError('Keine Abfangrakete verfügbar — baue ein Abfang-Raketensystem.');
  }
  state.planets[0]!.defense.interceptor -= 1;
  state.event = null;
  // XP reward scales with account level (GDD §2 lists comet defence as an XP source).
  events.push(...grantXp(state, 200 + 25 * state.level, now));
  events.push({ at: now, type: 'cometResolved', message: 'Komet abgefangen! Die Rakete hat ihr Ziel zerstört.', data: { via: 'intercept' } });
  rescheduleAfterEvent(state, now);
  return { state, events };
}

/** Raise the shield dome: no damage, no crater, dome survives. */
export function shieldComet(input: GameState, now: number): TickResult {
  const { state, events } = tick(input, now);
  const event = state.event;
  if (!event || event.type !== 'comet') throw new ActionError('Kein Komet im Anflug.');
  if (state.planets[0]!.defense.shieldDome < 1) {
    throw new ActionError('Keine Schildkuppel vorhanden.');
  }
  // A dome absorbs one impact and is consumed; multiple domes provide a
  // durable buffer without making a single purchase permanently invulnerable.
  state.planets[0]!.defense.shieldDome -= 1;
  state.event = null;
  events.push({ at: now, type: 'cometResolved', message: 'Schildkuppel aktiviert — der Komet verglüht am Schild.', data: { via: 'shield' } });
  rescheduleAfterEvent(state, now);
  return { state, events };
}

/**
 * Caps simultaneous fleet missions at `fleetHangarCapacity()` (Schiffshangar,
 * owner decision — see docs/ROADMAP.md Phase 4). Counts today's two mission
 * types; once Meilenstein F generalises them into `state.fleetMissions[]`,
 * this becomes a length check against the same array instead.
 */
function requireFleetCapacity(state: GameState): void {
  const capacity = fleetHangarCapacity(buildingLevel(state, 'shipHangar'));
  const active = state.fleetMissions.length;
  if (active >= capacity) {
    throw new ActionError(
      capacity === 0
        ? 'Du benötigst einen Schiffshangar, um Flotten auf Missionen zu schicken.'
        : `Schiffshangar-Kapazität erreicht (${active}/${capacity} Flotten unterwegs).`,
    );
  }
}

/**
 * Send miners to the asteroid. They are away for 30 min; yield is split
 * 60/40 titan/silicate and scales with Asteroiden-Bergbau (+15 %/level).
 */
export function harvestAsteroid(input: GameState, ships: number, now: number): TickResult {
  const { state, events } = tick(input, now);
  requireFleetCapacity(state);
  const event = state.event;
  if (!event || event.type !== 'asteroid') throw new ActionError('Kein Asteroid im Orbit.');
  if (ships < 1 || ships > state.planets[0]!.ships.minerShip) {
    throw new ActionError('Nicht genug Erz-Sammelschiffe im Hangar.');
  }

  const def = UNITS.minerShip;
  const techBonus = 1 + 0.15 * state.research.asteroidMining;
  const capacity = ships * (def.cargo ?? 0) * techBonus;
  const total = Math.min(capacity, event.size);

  state.planets[0]!.ships.minerShip -= ships;
  state.event = null;
  state.fleetMissions.push({
    kind: 'harvestAsteroid',
    id: state.nextId++,
    ships,
    returnsAt: now + travelMs(state, HARVEST_TRIP_MS, now, { minerShip: ships }),
    yield: { titan: Math.round(total * 0.6), silicate: Math.round(total * 0.4) },
  });
  rescheduleAfterEvent(state, now);
  return { state, events };
}

function rescheduleAfterEvent(state: GameState, from: number): void {
  const roll = nextRandom(state.rngSeed);
  state.rngSeed = roll.seed;
  state.nextEventAt = from + (4 + roll.value * 4) * 3_600_000;
}

/* ------------------------------------------------------------------ *
 * Debris harvesting (battle leftovers — GDD §3/§5)                    *
 * ------------------------------------------------------------------ */

/**
 * Send miners to the orbital debris field left by a battle. Same 30 min round
 * trip as an asteroid run; capacity and the Asteroiden-Bergbau bonus apply
 * identically, because it is the same ship doing the same job.
 */
export function harvestDebris(input: GameState, ships: number, now: number): TickResult {
  const { state, events } = tick(input, now);
  requireFleetCapacity(state);
  const total = state.debris.titan + state.debris.silicate;
  if (total <= 0) throw new ActionError('Kein Trümmerfeld im Orbit.');
  if (ships < 1 || ships > state.planets[0]!.ships.minerShip) {
    throw new ActionError('Nicht genug Erz-Sammelschiffe im Hangar.');
  }

  const capacity = ships * (UNITS.minerShip.cargo ?? 0) * (1 + 0.15 * state.research.asteroidMining);
  const collected = Math.min(capacity, total);
  // Take proportionally from both piles so the field drains evenly.
  const titanShare = state.debris.titan / total;
  const titan = Math.round(collected * titanShare);
  const silicate = Math.round(collected - titan);

  state.debris.titan = Math.max(0, state.debris.titan - titan);
  state.debris.silicate = Math.max(0, state.debris.silicate - silicate);
  state.planets[0]!.ships.minerShip -= ships;
  state.fleetMissions.push({
    kind: 'harvestDebris',
    id: state.nextId++,
    ships,
    returnsAt: now + travelMs(state, HARVEST_TRIP_MS, now, { minerShip: ships }),
    yield: { titan, silicate },
  });
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Merchant portal (GDD §1 Händler-Relaisstation, §4 offer card)       *
 * ------------------------------------------------------------------ */

function requireMarket(state: GameState): void {
  if (!marketUnlocked(state)) {
    throw new ActionError('Das Händler-Portal benötigt eine Händler-Relaisstation (Stufe 1).');
  }
}

/** Sell raw resources for Galactic Credits at the taxed market rate. */
export function sellResource(
  input: GameState,
  resource: TradeableResource,
  amount: number,
  now: number,
): TickResult {
  const { state, events } = tick(input, now);
  requireMarket(state);
  if (!Number.isFinite(amount) || amount <= 0) throw new ActionError('Ungültige Menge.');
  const rounded = Math.floor(amount);
  if (state.resources[resource] < rounded) {
    throw new ActionError('Nicht genug Rohstoffe für diesen Verkauf.');
  }
  const credits = sellValue(state, resource, rounded);
  if (credits <= 0) throw new ActionError('Die Menge ist zu klein für einen Verkauf.');

  state.resources[resource] -= rounded;
  state.resources.credit += credits;
  events.push({
    at: now,
    type: 'trade',
    message: `${rounded.toLocaleString('de-DE')} ${RESOURCE_LABELS[resource]} verkauft für ${credits.toLocaleString('de-DE')} Credits.`,
    data: { direction: 'sell', resource, amount: rounded, credits },
  });
  return { state, events };
}

/** Buy raw resources with credits, paying the merchant margin plus tax. */
export function buyResource(
  input: GameState,
  resource: TradeableResource,
  amount: number,
  now: number,
): TickResult {
  const { state, events } = tick(input, now);
  requireMarket(state);
  if (!Number.isFinite(amount) || amount <= 0) throw new ActionError('Ungültige Menge.');
  const rounded = Math.floor(amount);
  const price = buyPrice(state, resource, rounded);
  if (state.resources.credit < price) {
    throw new ActionError(`Nicht genug Credits (benötigt: ${price.toLocaleString('de-DE')}).`);
  }

  state.resources.credit -= price;
  state.resources[resource] += rounded;
  events.push({
    at: now,
    type: 'trade',
    message: `${rounded.toLocaleString('de-DE')} ${RESOURCE_LABELS[resource]} gekauft für ${price.toLocaleString('de-DE')} Credits.`,
    data: { direction: 'buy', resource, amount: rounded, credits: price },
  });
  return { state, events };
}

/** Accept one of the rotating bulk offers. */
export function acceptOffer(input: GameState, offerId: number, now: number): TickResult {
  const { state, events } = tick(input, now);
  requireMarket(state);
  // No separate expiry check: `tick` above rotates the board the moment
  // `refreshAt` passes, and offers expire exactly then — so an expired offer
  // is already gone from the list. A second guard here would be unreachable.
  const index = state.market.offers.findIndex((o) => o.id === offerId);
  if (index < 0) throw new ActionError('Dieses Angebot ist nicht mehr verfügbar.');
  const offer = state.market.offers[index]!;
  if (state.resources.credit < offer.priceCredits) {
    throw new ActionError(`Nicht genug Credits (benötigt: ${offer.priceCredits.toLocaleString('de-DE')}).`);
  }

  state.resources.credit -= offer.priceCredits;
  state.resources[offer.resource] += offer.amount;
  state.market.offers.splice(index, 1);
  events.push({
    at: now,
    type: 'trade',
    message: `Angebot angenommen: ${offer.amount.toLocaleString('de-DE')} ${RESOURCE_LABELS[offer.resource]}.`,
    data: { direction: 'offer', resource: offer.resource, amount: offer.amount, credits: offer.priceCredits },
  });
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Casino (GDD §4 — credit sink with level-gated daily limits)         *
 * ------------------------------------------------------------------ */

const CASINO_GAME_LABELS: Record<CasinoGame, string> = {
  slots: 'Die Slotmachine',
  highlow: 'High-Low',
  roulette: 'Asteroiden-Roulette',
};

function requireCasinoGame(state: GameState, game: CasinoGame): void {
  const minLevel = CASINO_GAME_MIN_LEVEL[game];
  if (buildingLevel(state, 'casino') < minLevel) {
    throw new ActionError(`${CASINO_GAME_LABELS[game]} benötigt ein Casino (Stufe ${minLevel}).`);
  }
}

function takeStake(state: GameState, stake: number, now: number): void {
  if (!Number.isInteger(stake) || stake < MIN_STAKE) {
    throw new ActionError(`Mindesteinsatz sind ${MIN_STAKE} Credits.`);
  }
  if (state.resources.credit < stake) throw new ActionError('Nicht genug Credits.');
  rolloverDay(state, now);
  const remaining = remainingStake(state, now);
  if (stake > remaining) {
    throw new ActionError(
      `Tages-Limit erreicht. Heute noch möglich: ${remaining.toLocaleString('de-DE')} Credits ` +
        `(Limit steigt mit dem Account-Level).`,
    );
  }
  state.resources.credit -= stake;
  state.casino.stakedToday += stake;
  state.casino.totalStaked += stake;
}

/** One slot-machine spin. */
export function playSlots(input: GameState, stake: number, now: number): TickResult {
  const { state, events } = tick(input, now);
  requireCasinoGame(state, 'slots');
  takeStake(state, stake, now);

  const spin = spinSlots(state.rngSeed, stake);
  state.rngSeed = spin.seed;
  state.resources.credit += spin.result.payout;
  state.casino.totalWon += spin.result.payout;

  events.push({
    at: now,
    type: 'casino',
    message:
      spin.result.kind === 'triple'
        ? `Dreifach ${spin.result.reels[0]}! Gewinn: ${spin.result.payout.toLocaleString('de-DE')} Credits.`
        : spin.result.kind === 'pair'
          ? `Paar getroffen. Gewinn: ${spin.result.payout.toLocaleString('de-DE')} Credits.`
          : `Kein Gewinn. Einsatz ${stake.toLocaleString('de-DE')} Credits verloren.`,
    data: { game: 'slots', ...spin.result, stake },
  });
  events.push(...rollCasinoBlueprint(state, now));
  return { state, events };
}

/** One High-Low bet on the resource price index. */
export function playHighLowBet(
  input: GameState,
  stake: number,
  guess: 'higher' | 'lower',
  current: number,
  now: number,
): TickResult {
  const { state, events } = tick(input, now);
  requireCasinoGame(state, 'highlow');
  takeStake(state, stake, now);

  const round = playHighLow(state.rngSeed, stake, guess, current);
  state.rngSeed = round.seed;
  state.resources.credit += round.result.payout;
  state.casino.totalWon += round.result.payout;

  events.push({
    at: now,
    type: 'casino',
    message: round.result.won
      ? `Kurs ${round.result.next} — richtig getippt! Gewinn: ${round.result.payout.toLocaleString('de-DE')} Credits.`
      : `Kurs ${round.result.next} — daneben. Einsatz verloren.`,
    data: { game: 'highlow', ...round.result, stake },
  });
  events.push(...rollCasinoBlueprint(state, now));
  return { state, events };
}

/**
 * Every casino round gets one blueprint roll. The GDD lists event-ship
 * blueprints among casino prizes, and the Void-Phantom is flagged event-only
 * in the catalogue — so this is the sole path from the wheel to a hangar.
 */
function rollCasinoBlueprint(state: GameState, now: number): EngineEvent[] {
  const roll = rollBlueprint(state.rngSeed);
  state.rngSeed = roll.seed;
  if (!roll.dropped) return [];
  state.blueprints.voidPhantom = (state.blueprints.voidPhantom ?? 0) + 1;
  return [
    {
      at: now,
      type: 'blueprint',
      message: 'Jackpot-Nebengewinn: Bauplan für ein Void-Phantom erbeutet!',
      data: { ship: 'voidPhantom' },
    },
  ];
}

/** One spin of the Asteroiden-Roulette (GDD §4). */
export function playRoulette(
  input: GameState,
  stake: number,
  bet: RouletteBet,
  now: number,
): TickResult {
  const { state, events } = tick(input, now);
  requireCasinoGame(state, 'roulette');
  if (bet.kind === 'sector' && (bet.sector < 0 || bet.sector > 12 || !Number.isInteger(bet.sector))) {
    throw new ActionError('Ungültiger Sektor — wähle 0 bis 12.');
  }
  takeStake(state, stake, now);

  const spin = spinRoulette(state.rngSeed, stake, bet);
  state.rngSeed = spin.seed;
  state.resources.credit += spin.result.payout;
  state.casino.totalWon += spin.result.payout;

  events.push({
    at: now,
    type: 'casino',
    message: spin.result.won
      ? `Sektor ${spin.result.sector} — Treffer! Gewinn: ${spin.result.payout.toLocaleString('de-DE')} Credits.`
      : `Sektor ${spin.result.sector} — verloren.`,
    data: { game: 'roulette', ...spin.result, stake },
  });
  events.push(...rollCasinoBlueprint(state, now));
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Chronos-Shop (GDD §2) — no payment provider yet; every purchase is  *
 * granted immediately as a placeholder for the eventual checkout.     *
 * ------------------------------------------------------------------ */

/**
 * Redeem a shop product. Grants resources/Chronos-Kristalle immediately —
 * there is no real payment step (docs/TODO.md), this is the "pretend it was
 * paid" placeholder the owner asked for. The daily Chronos cap (GDD §2
 * "Chronos-Kristall-Deckelung") is enforced regardless, so the mock shop
 * doesn't become a trivial infinite-currency exploit and the rule is already
 * correct once a real payment provider lands.
 */
export function purchaseProduct(input: GameState, productId: string, now: number, catalog: Record<string, ShopProduct> = SHOP_PRODUCTS): TickResult {
  const { state, events } = tick(input, now);
  const product = catalog[productId];
  if (!product) throw new ActionError('Unbekanntes Angebot.');

  if (product.oneTime && state.shop.redeemedOnce.includes(productId)) {
    throw new ActionError('Dieses Angebot wurde bereits eingelöst.');
  }

  rolloverShopDay(state, now);
  const grantedChronos = product.chronos ?? 0;
  if (grantedChronos > 0 && grantedChronos > remainingDailyChronos(state, now)) {
    throw new ActionError(
      `Tages-Kauflimit für Chronos-Kristalle erreicht (max. ${CHRONOS_DAILY_CAP.toLocaleString('de-DE')}/Tag). Versuch's morgen wieder.`,
    );
  }

  const grant = productGrant(state, now, product);
  addResources(state, grant);
  state.shop.chronosBoughtToday += grantedChronos;
  state.shop.totalChronosBought += grantedChronos;
  state.shop.totalSpentEur += product.priceEur;
  if (product.oneTime) state.shop.redeemedOnce.push(productId);

  events.push({
    at: now,
    type: 'shopPurchase',
    message: `${product.label} gekauft — vielen Dank für deine Unterstützung!`,
    data: { productId, ...grant },
  });
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Espionage & camp raids (GDD §3 NPC pirates, §5 step 4 spy rules)     *
 * ------------------------------------------------------------------ */

/**
 * Send Aero-Sonden to scout a camp.
 *
 * The GDD's rule verbatim: compare tech levels; on success return a summary of
 * resources and fleets, on failure destroy the probe. Probes are consumed
 * either way — a scout that comes home would make information free.
 */
export function scoutCamp(
  input: GameState,
  campId: number,
  probes: number,
  now: number,
): TickResult {
  const { state, events } = tick(input, now);
  requireFleetCapacity(state);
  const camp = state.camps.find((c) => c.id === campId);
  if (!camp) throw new ActionError('Dieses Lager existiert nicht mehr.');
  if (camp.scouted) throw new ActionError('Dieses Lager ist bereits ausgespäht.');
  if (!Number.isInteger(probes) || probes < 1) throw new ActionError('Ungültige Sondenzahl.');
  if (state.planets[0]!.ships.probe < probes) {
    throw new ActionError('Nicht genug Aero-Sonden im Hangar.');
  }

  state.planets[0]!.ships.probe -= probes;

  const legMs = fleetTravelTime(CAMP_TRAVEL_MS, { probe: probes }, computeModifiers(state, now).flightSpeed);
  state.fleetMissions.push({ kind: 'scoutCamp', id: state.nextId++, campId, probes, arrivesAt: now + legMs, returnsAt: now + legMs });
  return { state, events };
}

/** Sends probes to a player; the second save is inspected by the backend. */
export function scoutPlayerFleet(
  input: GameState,
  target: { ring: 1 | 2 | 3 | 4 | 5; system: number; position: number; targetLevel?: number },
  probes: number,
  now: number,
): TickResult {
  const { state, events } = tick(input, now);
  requireFleetCapacity(state);
  if (!canTargetInRing(state.level, target.ring, target.targetLevel ?? state.level)) throw new ActionError('Dieses Ziel liegt außerhalb deiner PvP-Schutzklasse.');
  if (!Number.isInteger(probes) || probes < 1 || state.planets[0]!.ships.probe < probes) throw new ActionError('Nicht genug Aero-Sonden im Hangar.');
  state.planets[0]!.ships.probe -= probes;
  const distance = travelDistance(state.planets[0]!, target);
  const baseMs = Math.max(PVP_DISTANCE_MS, Math.round(distance * PVP_DISTANCE_MS));
  const legMs = fleetTravelTime(baseMs, { probe: probes }, computeModifiers(state, now).flightSpeed);
  state.fleetMissions.push({ kind: 'scoutPlayer', id: state.nextId++, target: { ring: target.ring, system: target.system, position: target.position }, probes, arrivesAt: now + legMs, returnsAt: now + legMs * 2, resolutionRequested: false, resolved: false });
  return { state, events };
}

/** Send a combat fleet to attack a camp. Resolves on arrival (see tick). */
export function attackCamp(
  input: GameState,
  campId: number,
  fleet: Partial<Record<ShipKey, number>>,
  now: number,
): TickResult {
  const { state, events } = tick(input, now);
  requireFleetCapacity(state);
  const camp = state.camps.find((c) => c.id === campId);
  if (!camp) throw new ActionError('Dieses Lager existiert nicht mehr.');

  const entries = Object.entries(fleet).filter(([, n]) => (n ?? 0) > 0) as [ShipKey, number][];
  if (entries.length === 0) throw new ActionError('Wähle mindestens ein Schiff.');
  for (const [key, count] of entries) {
    if (!Number.isInteger(count) || count < 0) throw new ActionError('Ungültige Schiffszahl.');
    if (state.planets[0]!.ships[key] < count) {
      throw new ActionError(`Nicht genug ${UNITS[key].label} im Hangar.`);
    }
    if (key === 'probe') throw new ActionError('Aero-Sonden können nicht angreifen.');
  }

  // Isotop-3 fuel, reduced by the war talent band (GDD §2 tier 1–5).
  const fuel = estimateFuelCost(state, fleet);
  if (state.resources.isotope < fuel) {
    throw new ActionError(`Nicht genug Isotop-3 als Treibstoff (benötigt: ${fuel}).`);
  }
  state.resources.isotope -= fuel;

  const sent: Partial<Record<UnitKey, number>> = {};
  for (const [key, count] of entries) {
    state.planets[0]!.ships[key] -= count;
    sent[key] = count;
  }

  const legMs = travelMs(state, CAMP_TRAVEL_MS, now, sent);
  state.fleetMissions.push({
    kind: 'campAttack',
    id: state.nextId++,
    campId,
    campName: camp.name,
    fleet: sent,
    arrivesAt: now + legMs,
    returnsAt: now + legMs * 2,
    resolved: false,
  });
  return { state, events };
}

/** One distance unit is one minute of one-way PvP travel time. */
export const PVP_DISTANCE_MS = 60_000;

/** Sends a player attack; the backend resolves the two-save battle on arrival. */
export function sendAttackFleet(
  input: GameState,
  target: { ring: 1 | 2 | 3 | 4 | 5; system: number; position: number; targetLevel?: number },
  fleet: Partial<Record<ShipKey, number>>,
  now: number,
): TickResult {
  const { state, events } = tick(input, now);
  requireFleetCapacity(state);
  if (!canTargetInRing(state.level, target.ring, target.targetLevel ?? state.level)) {
    throw new ActionError('Dieses Ziel liegt außerhalb deiner PvP-Schutzklasse.');
  }
  const entries = Object.entries(fleet).filter(([, count]) => (count ?? 0) > 0) as [ShipKey, number][];
  if (entries.length === 0) throw new ActionError('Wähle mindestens ein Schiff.');
  for (const [key, count] of entries) {
    if (!Number.isInteger(count) || count < 0) throw new ActionError('Ungültige Schiffszahl.');
    if (key === 'probe' || key === 'colonyShip') throw new ActionError('Dieses Schiff kann nicht angreifen.');
    if (state.planets[0]!.ships[key] < count) throw new ActionError(`Nicht genug ${UNITS[key].label} im Hangar.`);
  }
  const fuel = estimateFuelCost(state, fleet);
  if (state.resources.isotope < fuel) throw new ActionError(`Nicht genug Isotop-3 als Treibstoff (benötigt: ${fuel}).`);
  state.resources.isotope -= fuel;
  const sent: Partial<Record<ShipKey, number>> = {};
  for (const [key, count] of entries) {
    state.planets[0]!.ships[key] -= count;
    sent[key] = count;
  }
  const distance = travelDistance(state.planets[0]!, target);
  const baseTravelMs = Math.max(PVP_DISTANCE_MS, Math.round(distance * PVP_DISTANCE_MS));
  const arrivesAt = now + fleetTravelTime(baseTravelMs, sent, computeModifiers(state, now).flightSpeed);
  state.fleetMissions.push({
    id: state.nextId++, kind: 'playerAttack',
    target: { ring: target.ring, system: target.system, position: target.position },
    fleet: sent, arrivesAt, returnsAt: arrivesAt * 2 - now, resolutionRequested: false, resolved: false,
  });
  return { state, events };
}

/** Isotop-3 burned per ship on a one-way camp run (design fill-in). Exported
 * for npc-colony.ts, which reuses it for the equivalent real-coordinate raid. */
export const FUEL_PER_SHIP: Record<ShipKey, number> = {
  probe: 1,
  minerShip: 10,
  transporter: 8,
  corvette: 4,
  cruiser: 15,
  battleship: 40,
  voidPhantom: 25,
  moonBreaker: 500,
  // Never actually spent — colonyShip is excluded from COMBAT_CAPABLE and
  // foundColony() doesn't charge fuel, only present so this stays exhaustive.
  colonyShip: 0,
};

/** Estimates one-way isotope cost for a selected fleet using current talents. */
export function estimateFuelCost(state: GameState, fleet: Partial<Record<ShipKey, number>>): number {
  const talents = talentEffects(state.talents);
  return Math.ceil(
    Object.entries(fleet).reduce((sum, [key, count]) => sum + (count ?? 0) * FUEL_PER_SHIP[key as ShipKey], 0) * talents.fuelFactor,
  );
}

/* ------------------------------------------------------------------ *
 * Colonization (galaxy plan Milestone E)                              *
 * ------------------------------------------------------------------ */

/** Endgame cap on owned planets (galaxy plan). */
export const MAX_PLANETS = 10;

/** Ring 2 ("Schwerkraft-Grenze", level 36–49 in RINGS) is where the GDD
 * (§3) first allows colonizing a second system. */
export const COLONY_MIN_LEVEL = RINGS[2].levelRange[0];

/**
 * Founds a new colony at `slot` — real galaxy coordinates a backend caller
 * already reserved via GalaxyService (a client can't be trusted to invent
 * its own unclaimed coordinates, so this always takes them as a parameter
 * rather than picking them itself, same reasoning as npc-colony.ts's
 * colony/garrison input). Consumes one Kolonieschiff from the home
 * planet's hangar; the ship never "travels" in this pass — no real
 * distance-based travel-time model exists yet for galaxy coordinates
 * (Meilenstein F).
 */
export function foundColony(
  input: GameState,
  slot: { ring: 1 | 2 | 3 | 4 | 5; system: number; position: number },
  now: number,
): TickResult {
  const { state, events } = tick(input, now);

  if (state.level < COLONY_MIN_LEVEL) {
    throw new ActionError(`Kolonisierung ab Level ${COLONY_MIN_LEVEL} (Ring 2) möglich.`);
  }
  if (state.planets.length >= MAX_PLANETS) {
    throw new ActionError(`Maximal ${MAX_PLANETS} Planeten erreichbar.`);
  }
  if (state.planets[0]!.ships.colonyShip < 1) {
    throw new ActionError('Kein Kolonieschiff im Hangar.');
  }
  state.planets[0]!.ships.colonyShip -= 1;

  const tempRoll = nextInt(state.rngSeed, -30, 20);
  state.rngSeed = tempRoll.seed;

  const planet = createColonyPlanet(
    `Kolonie ${state.planets.length + 1}`,
    slot.ring,
    slot.system,
    slot.position,
    tempRoll.value,
  );
  state.planets.push(planet);

  events.push({
    at: now,
    type: 'colonyFounded',
    message: `${planet.name} gegründet [${slot.ring}:${slot.system}:${slot.position}].`,
    data: { planetIndex: state.planets.length - 1, name: planet.name },
  });
  return { state, events };
}

/** Permanently removes one non-home colony. Its coordinates are released by
 * the server-side game service after the updated save has been persisted. */
export function abandonColony(input: GameState, planetIndex: number, now: number): TickResult {
  const { state, events } = tick(input, now);
  if (!Number.isInteger(planetIndex) || planetIndex < 0 || planetIndex >= state.planets.length) {
    throw new ActionError('Ungültige Kolonie ausgewählt.');
  }
  if (planetIndex === 0) throw new ActionError('Die Heimatwelt kann nicht aufgegeben werden.');
  state.planets.splice(planetIndex, 1);
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Talents                                                             *
 * ------------------------------------------------------------------ */

export function spendTalentPoint(input: GameState, path: TalentPathKey, now: number): TickResult {
  const { state, events } = tick(input, now);
  if (state.talentPoints < 1) throw new ActionError('Kein Talentpunkt verfügbar.');
  if (state.talents[path] >= TALENT_MAX_PER_PATH) {
    throw new ActionError('Dieser Pfad ist bereits voll ausgebaut.');
  }
  state.talentPoints -= 1;
  state.talents[path] += 1;
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Commander                                                           *
 * ------------------------------------------------------------------ */

export function renameCommander(input: GameState, name: string, now: number): TickResult {
  const { state, events } = tick(input, now);
  const trimmed = name.trim();
  if (trimmed.length < 2 || trimmed.length > 20) {
    throw new ActionError('Name muss 2–20 Zeichen lang sein.');
  }
  state.playerName = trimmed;
  return { state, events };
}

/* ------------------------------------------------------------------ *
 * Save/load                                                           *
 * ------------------------------------------------------------------ */

export interface SavedGame {
  format: 'spacegame-save';
  version: 1;
  state: GameState;
}

export function serialize(state: GameState): SavedGame {
  return { format: 'spacegame-save', version: 1, state: structuredClone(state) };
}

/**
 * Validates the envelope and normalises the state against the current
 * catalogues: any building/ship/defense/research key added to the game after
 * a save was written is filled in at 0. That makes catalogue growth (like the
 * storage buildings) a non-event for existing saves — no version bump needed
 * for purely additive content.
 */
export function deserialize(saved: unknown): GameState {
  const s = saved as SavedGame;
  if (!s || s.format !== 'spacegame-save' || s.version !== 1 || !s.state) {
    throw new ActionError('Ungültiger oder inkompatibler Spielstand.');
  }
  const state = structuredClone(s.state) as GameState & {
    /** Pre-galaxy-plan shape (single planet, empire-wide build/shipyard queues). */
    planet?: PlanetState;
    buildQueue?: QueueJob[];
    shipyardQueue?: QueueJob[];
    expedition?: import('./types.js').Expedition | null;
    harvest?: import('./types.js').HarvestMission | null;
  };
  // Galaxy plan Milestone A: promote a single `planet` + top-level build/
  // shipyard queues into `planets[0]`. Must run before every other backfill
  // below, since those read `state.planets[0]`.
  if (!state.planets && state.planet) {
    state.planets = [
      { ...state.planet, buildQueue: state.buildQueue ?? [], shipyardQueue: state.shipyardQueue ?? [] },
    ];
    delete state.planet;
    delete state.buildQueue;
    delete state.shipyardQueue;
  }
  // Subsystems added after a save was written get their empty shape back.
  state.debris ??= { titan: 0, silicate: 0 };
  state.market ??= { offers: [], refreshAt: state.lastTick };
  state.casino ??= { stakedToday: 0, dayStartedAt: state.lastTick, totalStaked: 0, totalWon: 0 };
  state.shop ??= {
    chronosBoughtToday: 0,
    dayStartedAt: state.lastTick,
    totalChronosBought: 0,
    totalSpentEur: 0,
    redeemedOnce: [],
  };
  state.blueprints ??= {};
  state.camps ??= [];
  state.campsRefreshAt ??= state.lastTick;
  if (!Array.isArray(state.fleetMissions)) {
    const legacy = state as GameState & { expedition?: import('./types.js').Expedition | null; harvest?: import('./types.js').HarvestMission | null };
    const missions: FleetMission[] = [];
    if (legacy.expedition) missions.push({ kind: 'campAttack', ...legacy.expedition });
    if (legacy.harvest) missions.push({ kind: 'harvestAsteroid', ...legacy.harvest });
    state.fleetMissions = missions;
  }
  delete state.expedition;
  delete state.harvest;
  state.missionIndex ??= 0;
  state.effects ??= [];
  if (!state.moon) {
    // Pre-moon saves kept the Phalanx on the planet; move it to where it belongs.
    const legacy = (state.planets[0]!.buildings as Partial<Record<BuildingKey, number>>).phalanx ?? 0;
    state.moon = { name: 'Basis-Mond', buildings: { phalanx: legacy } };
  }
  state.moon.buildings ??= {};
  for (const key of Object.keys(BUILDINGS) as BuildingKey[]) {
    state.planets[0]!.buildings[key] ??= 0;
  }
  for (const key of Object.keys(UNITS) as UnitKey[]) {
    if (UNITS[key].kind === 'ship') state.planets[0]!.ships[key as ShipKey] ??= 0;
    else state.planets[0]!.defense[key as DefenseKey] ??= 0;
  }
  for (const key of Object.keys(RESEARCH) as ResearchKey[]) {
    state.research[key] ??= 0;
  }
  return state;
}
